import * as _ from 'underscore';
import * as $ from 'jquery';

import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { apiCall } from '../../../../_common/platform/ts/AqlUtility';
import { Constants } from '../../../../_common/platform/ts/Constants';
import * as Utilities from '../../../../_common/platform/ts/Utilities';
import * as permissions from '../../../../_common/platform/ts/permissions';

import { config } from '../config';

import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';
import { Action, AppActions, I, check as checkAction } from '../actions/AppActions';

import * as Common from '../../tsx/menus/Common';

import { IGlob } from './../Glob';

export interface ISection {
    type: Common.MenuItemTypes;
    id: number;
    refId: number;
    parentSectionId: number;
}

export interface IFolderItem {
    dirid: number;
    name: string;
    subdirlist: IFolderItem[];
}

export interface IWorkspacesNavState {
    loading: boolean;

    menu: Common.IMenuBranch[];
    hideMenuItemIcons?: boolean;
    expandMenuOnMouseOver?: boolean;
    treeMode?: Common.TreeModes;
    folderTree?: IFolderItem[];
    currentSection?: ISection;
    expandedSectionsID?: number[];
    actionsMenuIsShown?: boolean;
}

export class WorkspacesNavStore extends BaseStore<IWorkspacesNavState, AppActions, Dispatcher, IGlob> {
    public _state: IWorkspacesNavState;
    public static _name = 'workspaces-nav';

    public static getInitialState(): IWorkspacesNavState {
        return {
            loading: true,
            menu: null,
            currentSection: null,
            expandedSectionsID: null,
            actionsMenuIsShown: false
        };
    }

    public serializeDataToStore(data: IWorkspacesNavState): string {
        const obj = {
            currentSection: data.currentSection,
            expandedSectionsID: data.expandedSectionsID
        };

        return JSON.stringify(obj);
    }

    private showSidebar() {
        this.workspaceCustomMenuLoad(true);
    }

    private workspaceCustomMenuLoad(reload: boolean) {
        if (reload === false && this._state.menu != null) {
            return;
        }

        if (this._state.menu == null) {
            this._state.menu = [];
        }

        this._state.loading = true;

        if (permissions.showSideBar()) {
            apiCall(this.actions, 'menu_GetCustomMenu', {})
                .done((resp) => {
                    const data = resp.result as Common.ICustomMenuValue;
                    const initialMenu = data.menu;
                    const treeMode = data.treeMode != null ? data.treeMode : Common.TreeModes.flat;

                    this.addDynamicMenuItems(initialMenu, treeMode, reload)
                        .done(() => {
                            const menu: Common.IMenuBranch[] = [];

                            let browseAllIsAdded = false;

                            if (initialMenu != null) {
                                initialMenu.forEach((item) => {
                                    if (this.checkPermission(item.type)) {
                                        if (item.parent == null) {
                                            menu.push(item);
                                        } else {
                                            const parentItems = Common.findMenuItems(menu, (menuItem) => {
                                                return menuItem.id === item.parent;
                                            });

                                            if (parentItems != null) {
                                                parentItems.forEach((parentItem) => {
                                                    if (parentItem.items == null) {
                                                        parentItem.items = [];
                                                    }

                                                    parentItem.items.push(item);
                                                });
                                            } else {
                                                menu.push(item);
                                            }
                                        }

                                        if (!browseAllIsAdded && item.type === Common.BROWSE_ALL) {
                                            browseAllIsAdded = true;
                                        }
                                    }
                                });
                            }

                            if (!browseAllIsAdded && this.checkPermission(Common.BROWSE_ALL)) {
                                menu.push({
                                    type: Common.BROWSE_ALL,
                                    name: null,
                                    id: -1,
                                    level: 0
                                });
                            }

                            if (this._state.expandedSectionsID != null && this._state.expandedSectionsID.length > 0) {
                                this.setExpandedState(menu, this._state.expandedSectionsID);
                            }

                            this._state.menu = menu;
                            this._state.hideMenuItemIcons = data.hideMenuItemIcons;
                            this._state.expandMenuOnMouseOver = data.expandMenuOnMouseOver;
                            this._state.treeMode = treeMode;
                        })
                        .always(() => {
                            this.actions.workspaceCustomMenuLoadFinish();
                        });
                })
                .fail(() => {
                    this.actions.workspaceCustomMenuLoadFinish();
                });
        }
    }

    private checkPermission(type: Common.MenuItemTypes) {
        switch (type) {
            case Common.FAVORITES:
                return permissions.showSideBarFavourites();

            case Common.RECENT:
                return permissions.showSideBarRecent();

            case Common.MY_FOLDER:
                return permissions.showSideBarMyOwn();

            case Common.BROWSE_ALL:
                return permissions.showSideBarBrowse();

            default:
                return true;
        }
    }

    private setExpandedState(menu: Common.IMenuBranch[], expandedSectionsID: number[]) {
        _.each(menu, (item) => {
            if (expandedSectionsID.indexOf(item.id) >= 0) {
                item.isExpanded = true;
            }

            if (item.items != null) {
                this.setExpandedState(item.items, expandedSectionsID);
            }
        });
    }

    private addDynamicMenuItems(
        menu: Common.IMenuBranch[],
        treeMode: Common.TreeModes,
        reload: boolean
    ): JQueryPromise<any> {
        const result = $.Deferred();

        if (treeMode !== Common.TreeModes.tree) {
            const myFolders = Common.findMenuItems(menu, (menuItem) => {
                return menuItem.type === Common.MY_FOLDER;
            });

            _.each(myFolders, (item) => {
                item.refId = this.glob.AuthStore._state.homeFolderId;
            });

            result.resolve();
            return result.promise();
        }

        let maxID = 0;
        let addDynamicSubItems = false;

        _.each(menu, (item) => {
            if (item.id > maxID) {
                maxID = item.id;
            }

            if (item.type === Common.MY_FOLDER) {
                item.refId = this.glob.AuthStore._state.homeFolderId;
            }

            if (!addDynamicSubItems && this.hasDynamicSubItems(item.type)) {
                addDynamicSubItems = true;
            }
        });

        const resolve = (menu: Common.IMenuBranch[], maxID: number) => {
            _.each(menu, (menuItem) => {
                if (this.hasDynamicSubItems(menuItem.type)) {
                    const folders = this.findFolders(this._state.folderTree, menuItem.refId);

                    _.each(folders, (folder) => {
                        maxID = this.createDynamicMenuItems(menu, folder, menuItem.id, menuItem.level, maxID);
                    });
                }
            });

            result.resolve();
        };

        if (addDynamicSubItems) {
            if (this._state.folderTree == null || reload) {
                this.loadFolderTree()
                    .done(() => {
                        resolve(menu, maxID);
                    })
                    .fail(() => {
                        result.fail(() => {});
                    });
            } else {
                resolve(menu, maxID);
            }
        } else {
            resolve(menu, maxID);
        }

        return result.promise();
    }

    private hasDynamicSubItems(menuItemType: Common.MenuItemTypes) {
        return menuItemType === Common.MY_FOLDER || menuItemType === Common.DIR_BROWSER;
    }

    private createDynamicMenuItems(
        menu: Common.IMenuBranch[],
        folder: IFolderItem,
        parentID: number,
        parentLevel: number,
        maxID: number
    ): number {
        if (folder.subdirlist != null && folder.subdirlist.length > 0) {
            _.each(folder.subdirlist, (subFolder) => {
                const id = ++maxID;
                const level = parentLevel + 1;

                menu.push({
                    id: id,
                    type: Common.DIR_BROWSER,
                    name: subFolder.name,
                    parent: parentID,
                    refId: subFolder.dirid,
                    level: level
                });

                maxID = this.createDynamicMenuItems(menu, subFolder, id, level, maxID);
            });
        }

        return maxID;
    }

    private findFolders(tree: IFolderItem[], folderID: number, result?: IFolderItem[]): IFolderItem[] {
        if (result == null) {
            result = [];
        }

        _.each(tree, (folder) => {
            if (folder.dirid === folderID) {
                result.push(folder);
            } else {
                this.findFolders(folder.subdirlist, folderID, result);
            }
        });

        return result;
    }

    private workspaceCustomMenuLoadFinish() {
        this._state.loading = false;
    }

    private sortFolderTree(menu: IFolderItem[]): IFolderItem[] {
        if (menu == null || menu.length === 0) {
            return menu;
        }

        _.each(menu, (menuItem) => {
            if (menuItem.subdirlist != null && menuItem.subdirlist.length > 0) {
                menuItem.subdirlist = this.sortFolderTree(menuItem.subdirlist);
            }
        });

        return Utilities.sortByName<IFolderItem>(menu);
    }

    private loadFolderTree() {
        const def = apiCall<IFolderItem[]>(this.actions, 'dir_GetSubDirTree', {
            dirid: config.WIDGETS_ROOT_DIR_ID
        });

        def.done((resp) => {
            this._state.folderTree = this.sortFolderTree(resp.result);
        });

        return def;
    }

    private setSection(menuItem: Common.IMenuItem, mode: Common.MenuModes) {
        const menuItemID = menuItem.id;

        if (mode === Common.MenuModes.sideBar) {
            if (this._state.expandedSectionsID == null) {
                this._state.expandedSectionsID = [];
            }

            const stateMenuItems = Common.findMenuItems(this._state.menu, (menuItem) => {
                return menuItem.id === menuItemID;
            });
            const expandedSectionIndex = this._state.expandedSectionsID.indexOf(menuItemID);

            if (expandedSectionIndex < 0) {
                _.each(stateMenuItems, (item) => {
                    item.isExpanded = true;
                });

                this._state.expandedSectionsID.push(menuItemID);
            } else {
                _.each(stateMenuItems, (item) => {
                    item.isExpanded = false;
                });

                this._state.expandedSectionsID.splice(expandedSectionIndex, 1);
            }
        } else {
            if (this._state.currentSection == null || this._state.currentSection.id !== menuItemID) {
                const sectionType = menuItem.type;
                const refId =
                    sectionType !== Common.MY_FOLDER ? menuItem.refId : this.glob.AuthStore._state.homeFolderId;

                this._state.currentSection = {
                    type: sectionType,
                    id: menuItemID,
                    refId: refId,
                    parentSectionId: menuItem.parent
                };
            }
        }
    }

    private setActionsMenuIsShown(value: boolean) {
        this._state.actionsMenuIsShown = value;
    }

    private renameFinish(id: number, name: string, isNew: boolean) {
        if (this._state.treeMode !== Common.TreeModes.tree) {
            return;
        }

        if (isNew) {
            this.reloadTree();
        } else {
            const menuItems = Common.findMenuItems(this._state.menu, (menuItem) => {
                return menuItem.refId === id;
            });
            _.each(menuItems, (item) => {
                item.name = name;
            });

            const folders = this.findFolders(this._state.folderTree, id);
            _.each(folders, (item) => {
                item.name = name;
            });
        }
    }

    private reloadTree(success?: boolean) {
        if (success === false || this._state.treeMode !== Common.TreeModes.tree) {
            return;
        }

        this.workspaceCustomMenuLoad(true);
        this.emitChange();
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const a = payload.action;
        const _case = checkAction;

        do {
            if (_case<I.WORKSPACE_SHOW_SIDEBAR>(a, Constants.WORKSPACE_SHOW_SIDEBAR)) {
                this.showSidebar();
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACES_NAV_SET_SECTION>(a, Constants.WORKSPACES_NAV_SET_SECTION)) {
                this.setSection(a.menuItem, a.mode);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_CUSTOM_MENU_LOAD>(a, Constants.WORKSPACE_CUSTOM_MENU_LOAD)) {
                this.workspaceCustomMenuLoad(a.reload);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_CUSTOM_MENU_LOAD_FINISH>(a, Constants.WORKSPACE_CUSTOM_MENU_LOAD_FINISH)) {
                this.workspaceCustomMenuLoadFinish();
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_SET_ACTIONS_MENU_IS_SHOWN>(a, Constants.WORKSPACE_SET_ACTIONS_MENU_IS_SHOWN)) {
                this.setActionsMenuIsShown(a.value);
                //It is only a flag that an Ation Menu is shown. Store's change is not to be emitted.
                break;
            }

            if (_case<I.DIRS_RENAME_FINISH>(a, Constants.DIRS_RENAME_FINISH)) {
                this.renameFinish(a.id, a.name, a.isNew);
                break;
            }

            if (_case<I.DIRS_DELETE_FINISH>(a, Constants.DIRS_DELETE_FINISH)) {
                this.reloadTree(a.success);
                break;
            }

            if (_case<I.DIRS_MOVE_FINISH>(a, Constants.DIRS_MOVE_FINISH)) {
                this.reloadTree(a.success);
                break;
            }

            if (_case<I.DIRS_CLEAN_CACHE>(a, Constants.DIRS_CLEAN_CACHE)) {
                this.reloadTree();
                break;
            }
        } while (false);

        return true;
    }

    constructor(glob, dispatcher: Dispatcher, actions: AppActions) {
        super(glob, dispatcher, actions, WorkspacesNavStore._name);

        this._state = _.extend(WorkspacesNavStore.getInitialState(), this._state);
    }

    static get(glob, dispatcher: Dispatcher, actions: AppActions) {
        return new WorkspacesNavStore(glob, dispatcher, actions);
    }
}
